import './styles.scss'

import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Col, Form, Input, Row } from 'antd'
import { toast } from 'sonner'

import B2becTranslation from '@/components/B2becTranslation'
import CustomButton from '@/components/CustomButton'
import { ChevronLeftIcon } from '@/components/Icons'
import { fapiUserService } from '@/services/fapi'
import { useAppSelector } from '@/store/hook'
import { selectUserId } from '@/store/slices/authenticationSlice'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useBreadcrumbs from '@/hooks/useBreadcrumbs'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import { PAGE_TYPES } from '@/libs/constants/adobeAnalytics'
import { linkGenerator } from '@/libs/utils/language'
import {
  validateConfirmPassword,
  validatePasswordRequirements,
  validateRequiredMessage,
} from '@/libs/utils/passwordValidators'

const ERROR_TYPES = {
  INCORRECT_PASSWORD: 'INCORRECT_PASSWORD',
  DEFAULT: 'DEFAULT',
}

const ERROR_MESSAGES = {
  INVALID_PASSWORD: 'The password provided is invalid',
}

const isInvalidPasswordError = (text) => {
  return text?.toLowerCase()?.includes(ERROR_MESSAGES.INVALID_PASSWORD?.toLowerCase())
}

const errorTypeParse = (error) => {
  const response = error?.response

  if (response?.status === 500) {
    const errorDetail = response.data?.detail || ''

    try {
      const parsedDetail = JSON.parse(errorDetail)
      const message = parsedDetail?.message || ''

      if (isInvalidPasswordError(message)) {
        return ERROR_TYPES.INCORRECT_PASSWORD
      }
    } catch (parseError) {
      console.error('Failed to parse error detail:', parseError)
      if (isInvalidPasswordError(errorDetail)) {
        return ERROR_TYPES.INCORRECT_PASSWORD
      }
    }

    const errorCode = response.data?.errorCode
    return errorCode || ERROR_TYPES.DEFAULT
  }

  return ERROR_TYPES.DEFAULT
}

const { useForm } = Form

const SetPasswordPage = ({ breadcrumbs }) => {
  const [form] = useForm()
  useDocumentTitle('pageTitle.changePassword')
  useBreadcrumbs(breadcrumbs)

  useAdobeAnalysis(null, {
    pageName: 'change password',
    pageType: PAGE_TYPES.PROFILE,
    pageId: '17',
  })

  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const userId = useAppSelector(selectUserId)

  const handleChangePassword = useCallback(
    ({ currentPassword: oldPassword, newPassword }) =>
      fapiUserService.setNewPasswordApi(userId, oldPassword, newPassword),
    [userId],
  )

  const cancelChangeHandler = () => navigate(linkGenerator('/profile'))

  const onSubmit = async (submitValues) => {
    try {
      setIsLoading(true)
      await handleChangePassword(submitValues)
      setIsLoading(false)

      toast.success(t('setPassword.messages.success'))

      navigate(linkGenerator('/my-workspace'), { replace: true })
    } catch (e) {
      setIsLoading(false)
      const typeError = errorTypeParse(e)

      if (typeError !== ERROR_TYPES.INCORRECT_PASSWORD) {
        setError(typeError)

        return
      }

      form.setFields([
        {
          name: 'currentPassword',
          errors: [
            <B2becTranslation
              key="currentPasswordIncorrect"
              value="setPassword.validationMessage.currentPasswordIncorrect"
            />,
          ],
        },
      ])
    }
  }

  return (
    <>
      <div className="set-password-form__title-wrapper">
        <h1>{t('setPassword.title')}</h1>
        <CustomButton
          buttonSize="small"
          className="set-password-form__btn--back"
          onClick={() => cancelChangeHandler()}
        >
          <ChevronLeftIcon style={{ marginRight: 10 }} />
          {t('searchResultsPage.button.backToOverview')}
        </CustomButton>
        <p className="set-password-form__title-wrapper--message">
          {t('setPassword.message')}
        </p>
      </div>

      {error
        ? (
          <div className="set-password-form__error-message validate-message">
            <B2becTranslation
              value="setPassword.validationMessage.errorMessageWithCode"
              variables={{ errorCode: error }}
            />
          </div>
        )
        : null}

      <div className="set-password-form__container">
        <Form
          scrollToFirstError
          form={form}
          layout="vertical"
          name="set-password-form__form"
          onFinish={onSubmit}
        >
          <Row gutter={16}>
            <Col className="gutter-row" span={6}>
              <Form.Item
                className="custom-form-item"
                label={t('setPassword.label.currentPassword')}
                name="currentPassword"
                rules={[
                  {
                    required: true,
                    message: validateRequiredMessage(
                      'setPassword.label.currentPassword',
                    ),
                  },
                ]}
              >
                <Input.Password
                  autoComplete="currentPassword"
                  className="set-password-form__input"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col className="gutter-row" span={6}>
              <Form.Item
                validateFirst
                className="custom-form-item"
                label={t('setPassword.label.newPassword')}
                name="newPassword"
                rules={[
                  {
                    required: true,
                    message: validateRequiredMessage(
                      'setPassword.label.newPassword',
                    ),
                  },
                  {
                    validator: validatePasswordRequirements,
                  },
                ]}
              >
                <Input.Password
                  autoComplete="newPassword"
                  className="set-password-form__input"
                />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={6}>
              <Form.Item
                validateFirst
                className="custom-form-item"
                dependencies={['newPassword']}
                label={t('setPassword.label.confirmPassword')}
                name="confirmPassword"
                rules={[
                  {
                    required: true,
                    message: validateRequiredMessage(
                      'setPassword.label.confirmPassword',
                    ),
                  },
                  ({ getFieldValue }) => validateConfirmPassword(getFieldValue('newPassword')),
                ]}
              >
                <Input.Password
                  autoComplete="newPassword"
                  className="set-password-form__input"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <div className="set-password-form__buttons-wrapper">
              <CustomButton
                className="btn btn--submit"
                htmlType="submit"
                type="primary"
                disabled={isLoading}
                loading={isLoading}
              >
                {t('setPassword.title')}
              </CustomButton>
            </div>
          </Form.Item>
        </Form>
      </div>
    </>
  )
}

export default SetPasswordPage
